import React, { useState } from "react";
import { Box, Typography } from "@mui/material";
import dayjs from "dayjs";

/**
 * Weekly version of the MAU growth-accounting chart:
 * WAU(t) − WAU(t − 1 week) = new(t) + resurrected(t) − churned(t).
 * Bars are the three terms (new and resurrected stacked above zero, churned
 * below). The right axis, zero-aligned with the bars, carries the quick
 * ratio and the retention rate (retained ÷ prior week's actives).
 */

const COLORS = {
  churned: "#8fd14f",
  resurrected: "#e25b4a",
  newUsers: "#4f8fd6",
  quickRatio: "#6d3d91",
  retention: "#3db8c9",
  grid: "#ececec",
  zero: "#888",
};

const niceCeil = (value) => {
  if (!(value > 0)) return 1;
  const pow = 10 ** Math.floor(Math.log10(value));
  const n = value / pow;
  const step = n <= 1 ? 1 : n <= 2 ? 2 : n <= 2.5 ? 2.5 : n <= 5 ? 5 : 10;
  return step * pow;
};

/** Oldest week first. `quick` is null when nothing churned (ratio is infinite). */
export const toChartPoints = (weeks) =>
  [...weeks].reverse().map((week) => {
    const prevActive = week.active - week.net;
    const inflow = week.newUsers + week.resurrected;
    return {
      ...week,
      label: dayjs(week.weekStart).format("M/D"),
      inflow,
      retention: prevActive > 0 ? week.retained / prevActive : null,
      quick: week.churned === 0 ? null : inflow / week.churned,
    };
  });

const GrowthAccountingChart = ({ weeks }) => {
  const [hover, setHover] = useState(null);
  const points = toChartPoints(weeks || []);
  if (!points.length) return null;

  const W = 960;
  const H = 440;
  const pad = { top: 12, right: 52, bottom: 64, left: 58 };
  const plotW = W - pad.left - pad.right;
  const plotH = H - pad.top - pad.bottom;
  const zeroY = pad.top + plotH / 2;

  const countMax = niceCeil(
    Math.max(
      ...points.map((p) => Math.max(p.inflow, p.isPartial ? 0 : p.churned)),
      1,
    ),
  );
  const ratioPeak = Math.max(
    2,
    ...points.flatMap((p) => [p.quick, p.retention].filter((v) => v != null && v <= 8)),
  );
  const ratioMax = niceCeil(Math.min(ratioPeak, 8));

  const yCount = (v) => zeroY - (v / countMax) * (plotH / 2);
  const yRatio = (v) => zeroY - (Math.min(v, ratioMax) / ratioMax) * (plotH / 2);

  const slot = plotW / points.length;
  const barW = Math.max(6, Math.min(36, slot * 0.55));

  const countStep = countMax / 2;
  const countTicks = [countMax, countStep, 0, -countStep, -countMax];
  const ratioStep = ratioMax <= 2 ? 0.5 : ratioMax <= 5 ? 1 : Math.ceil(ratioMax / 4);
  const ratioTickSet = new Set([0, ratioMax, -ratioMax]);
  for (let v = ratioStep; v < ratioMax - 1e-9; v += ratioStep) {
    ratioTickSet.add(Math.round(v * 10) / 10);
    ratioTickSet.add(Math.round(-v * 10) / 10);
  }
  const ratioTicks = [...ratioTickSet].sort((a, b) => a - b);

  const lineSegments = (key) => {
    const segments = [];
    let d = "";
    points.forEach((p, i) => {
      const v = p[key];
      if (v == null) {
        if (d) segments.push(d);
        d = "";
        return;
      }
      const x = pad.left + slot * i + slot / 2;
      const cmd = `${d ? "L" : "M"} ${x} ${yRatio(v)}`;
      d = d ? `${d} ${cmd}` : cmd;
    });
    if (d) segments.push(d);
    return segments;
  };

  const formatTick = (v) => {
    const abs = Math.abs(v);
    if (abs >= 1000) return `${v < 0 ? "-" : ""}${Math.round(abs / 100) / 10}k`;
    return Number.isInteger(v) ? String(v) : v.toFixed(1);
  };

  return (
    <Box
      sx={{
        border: "1px solid #e0e0e0",
        borderRadius: "8px",
        padding: "12px 8px 4px",
        marginBottom: "16px",
        background: "#fff",
      }}
    >
      <Typography
        sx={{ fontWeight: 700, textAlign: "center", color: "#142C5C", fontSize: "15px" }}
      >
        Weekly Growth Accounting
      </Typography>
      <Typography
        sx={{
          textAlign: "center",
          color: "text.secondary",
          fontSize: "12px",
          marginBottom: "6px",
        }}
      >
        WAU(t) − WAU(t − 1 week) = new(t) + resurrected(t) − churned(t)
      </Typography>
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          gap: 2,
          flexWrap: "wrap",
          marginBottom: "4px",
        }}
      >
        {[
          [COLORS.churned, "Churned", false],
          [COLORS.resurrected, "Resurrected", false],
          [COLORS.newUsers, "New", false],
          [COLORS.quickRatio, "(new + res.) / churned", true],
          [COLORS.retention, "Retention rate", true],
        ].map(([color, label, line]) => (
          <Box
            key={label}
            sx={{ display: "flex", alignItems: "center", gap: 0.75, fontSize: "12px", color: "#555" }}
          >
            <Box
              sx={{
                width: 14,
                height: line ? 3 : 10,
                background: color,
                borderRadius: "2px",
              }}
            />
            {label}
          </Box>
        ))}
      </Box>
      <Box sx={{ position: "relative" }}>
        <svg
          viewBox={`0 0 ${W} ${H}`}
          width="100%"
          role="img"
          aria-label="Weekly growth accounting: new and resurrected above zero, churned below, quick ratio and retention rate on the right axis"
        >
          {countTicks.map((tick) => (
            <g key={`c${tick}`}>
              <line
                x1={pad.left}
                x2={W - pad.right}
                y1={yCount(tick)}
                y2={yCount(tick)}
                stroke={tick === 0 ? COLORS.zero : COLORS.grid}
              />
              <text x={pad.left - 8} y={yCount(tick) + 4} textAnchor="end" fontSize="12" fill="#777">
                {formatTick(tick)}
              </text>
            </g>
          ))}
          {ratioTicks.map((tick) => (
            <text
              key={`r${tick}`}
              x={W - pad.right + 8}
              y={yRatio(tick) + 4}
              fontSize="12"
              fill="#777"
            >
              {Number.isInteger(tick) ? tick : tick.toFixed(1)}
            </text>
          ))}
          {points.map((p, i) => {
            const x = pad.left + slot * i + (slot - barW) / 2;
            const cx = pad.left + slot * i + slot / 2;
            const newH = (p.newUsers / countMax) * (plotH / 2);
            const resH = (p.resurrected / countMax) * (plotH / 2);
            const churnH = p.isPartial ? 0 : (p.churned / countMax) * (plotH / 2);
            return (
              <g key={p.weekStart} opacity={p.isPartial ? 0.6 : 1}>
                <rect x={x} y={zeroY - newH - resH} width={barW} height={resH} fill={COLORS.resurrected} />
                <rect x={x} y={zeroY - newH} width={barW} height={newH} fill={COLORS.newUsers} />
                {!p.isPartial && (
                  <rect x={x} y={zeroY} width={barW} height={churnH} fill={COLORS.churned} />
                )}
                <text
                  x={cx}
                  y={H - 28}
                  textAnchor="end"
                  fontSize="12"
                  fill="#666"
                  transform={`rotate(-45 ${cx} ${H - 28})`}
                >
                  {p.label}
                  {p.isPartial ? "*" : ""}
                </text>
                <rect
                  x={pad.left + slot * i}
                  y={pad.top}
                  width={slot}
                  height={plotH}
                  fill="transparent"
                  onMouseEnter={() => setHover(i)}
                  onMouseLeave={() => setHover(null)}
                />
              </g>
            );
          })}
          {lineSegments("quick").map((d, i) => (
            <path
              key={`q${i}`}
              d={d}
              fill="none"
              stroke={COLORS.quickRatio}
              strokeWidth="2.5"
              pointerEvents="none"
            />
          ))}
          {lineSegments("retention").map((d, i) => (
            <path
              key={`t${i}`}
              d={d}
              fill="none"
              stroke={COLORS.retention}
              strokeWidth="2.5"
              pointerEvents="none"
            />
          ))}
          {points.map((p, i) => {
            const x = pad.left + slot * i + slot / 2;
            return (
              <g key={`d${p.weekStart}`} pointerEvents="none">
                {p.quick != null && (
                  <circle cx={x} cy={yRatio(p.quick)} r="3.5" fill={COLORS.quickRatio} />
                )}
                {p.retention != null && (
                  <circle cx={x} cy={yRatio(p.retention)} r="3.5" fill={COLORS.retention} />
                )}
              </g>
            );
          })}
        </svg>
        {hover != null && points[hover] && (
          <Box
            sx={{
              position: "absolute",
              left: `${((pad.left + slot * hover + slot / 2) / W) * 100}%`,
              top: 4,
              transform: "translateX(-50%)",
              background: "#142C5C",
              color: "#fff",
              fontSize: "12px",
              lineHeight: 1.5,
              borderRadius: "6px",
              padding: "8px 10px",
              pointerEvents: "none",
              zIndex: 1,
            }}
          >
            <div>
              {dayjs(points[hover].weekStart).format("MMM D")} –{" "}
              {dayjs(points[hover].weekEnd).format("MMM D")}
              {points[hover].isPartial ? " (in progress)" : ""}
            </div>
            <div>New {points[hover].newUsers.toLocaleString()}</div>
            <div>Resurrected {points[hover].resurrected.toLocaleString()}</div>
            <div>
              Churned{" "}
              {points[hover].isPartial
                ? "—"
                : points[hover].churned.toLocaleString()}
            </div>
            <div>
              Net{" "}
              {points[hover].net > 0
                ? `+${points[hover].net.toLocaleString()}`
                : points[hover].net.toLocaleString()}
            </div>
            <div>
              Quick ratio {points[hover].quick == null ? "∞" : points[hover].quick.toFixed(2)}
            </div>
            <div>
              Retention{" "}
              {points[hover].retention == null
                ? "—"
                : `${Math.round(points[hover].retention * 100)}%`}
            </div>
          </Box>
        )}
      </Box>
      {points.some((p) => p.isPartial) && (
        <Typography sx={{ fontSize: "11px", color: "text.secondary", textAlign: "right" }}>
          * week still in progress
        </Typography>
      )}
    </Box>
  );
};

export default GrowthAccountingChart;
